import type { TFunction } from 'i18next';
import type { ApprovalAttributes } from '@/types/approval';
import {
  SERP_NOT_LOADED_MESSAGE,
  type SerpDocumentRef,
} from '@/utils/approval/serpDocument';

/** SERP 폐점 정산 — 레거시 appr_edit_forms_serp_shop_close */
export const SERP_STORE_CLOSURE_TYPE = 181;

export const isSerpStoreClosureType = (apvType: number): boolean =>
  apvType === SERP_STORE_CLOSURE_TYPE;

/** EAI 응답 원문 — 저장 attributes.data 에 손대지 않고 그대로 보관 */
export type SerpStoreClosureSource = {
  BRAND_NM?: unknown;
  SHOP_NM?: unknown;
  SHOP_CD?: unknown;
  REP_NM?: unknown;
  BRN?: unknown;
  OUS_DE?: unknown;
  CUS_DE?: unknown;
  SPY_AMT?: unknown;
  DPST_AMT?: unknown;
  SPY_BAL_AMT?: unknown;
  GRT_AMT?: unknown;
  STMT_BAL_AMT?: unknown;
  SHOP_CTRT_TYP_NM?: unknown;
  PROD_TRD_TYP_NM?: unknown;
  CMS_DIV_NM?: unknown;
  [key: string]: unknown;
};

export type SerpStoreClosureDraftSource = {
  code: number;
  attributes: SerpDocumentRef;
  display: { source?: SerpStoreClosureSource | null };
};

export type SerpStoreClosureState = SerpDocumentRef & {
  source: SerpStoreClosureSource;
  /** SERP 응답으로 자동 입력한 제목 — 이탈 경고는 이 값과 비교. 편집은 미사용 */
  autoSubject: string;
};

// 레거시는 템플릿 문자열로 이어 붙여 빈 값이 'null'·'undefined' 글자로 찍힘(사용자 결정: 그대로)
const joinLegacy = (value: unknown) => `${value}`;

export const buildSerpStoreClosureShopName = (
  source: SerpStoreClosureSource,
): string => `${joinLegacy(source.SHOP_NM)}(${joinLegacy(source.SHOP_CD)})`;

export const buildSerpStoreClosurePeriod = (
  source: SerpStoreClosureSource,
): string => `${joinLegacy(source.OUS_DE)} ~ ${joinLegacy(source.CUS_DE)}`;

export const buildSerpStoreClosureOperationType = (
  source: SerpStoreClosureSource,
): string =>
  `${joinLegacy(source.SHOP_CTRT_TYP_NM)} - ${joinLegacy(
    source.PROD_TRD_TYP_NM,
  )} / ${joinLegacy(source.CMS_DIV_NM)}`;

export const buildSerpStoreClosureSubject = (
  source: SerpStoreClosureSource,
  t: TFunction,
): string =>
  t('폐점 정산의 건 ({{shop}})', {
    shop: buildSerpStoreClosureShopName(source),
    interpolation: { escapeValue: false },
  });

export const checkSerpStoreClosureBeforeSave = (
  state: SerpStoreClosureState | null | undefined,
): string | null => (state?.source ? null : SERP_NOT_LOADED_MESSAGE);

export const buildSerpStoreClosureAttributes = (
  state: SerpStoreClosureState | null | undefined,
  content: string,
): ApprovalAttributes | null =>
  state?.source
    ? {
        typeCode: state.typeCode,
        sequence: state.sequence,
        content,
        data: state.source,
      }
    : null;
